import { Link } from "wouter";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  NotebookPen,
  Sparkles,
  Languages,
  Plus,
  type LucideIcon,
} from "lucide-react";
import { trackAppDownload, trackCTA } from "@/lib/gtag";

/* ------------------------------------------------------------------ */
/* Accent system — full class names so Tailwind keeps them            */
/* cyan = translator, emerald = notes, purple = agent                 */
/* ------------------------------------------------------------------ */

export type Accent = "emerald" | "cyan" | "purple";

export const ACCENT: Record<
  Accent,
  {
    text: string;
    soft: string;
    border: string;
    glow: string;
    solid: string;
    solidText: string;
    ring: string;
    gradFrom: string;
  }
> = {
  emerald: {
    text: "text-emerald-400",
    soft: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    glow: "bg-emerald-500/20",
    solid: "bg-emerald-500",
    solidText: "text-[#0A111C]",
    ring: "ring-emerald-500/20",
    gradFrom: "from-emerald-500/15",
  },
  cyan: {
    text: "text-cyan-400",
    soft: "bg-cyan-500/10",
    border: "border-cyan-500/30",
    glow: "bg-cyan-500/20",
    solid: "bg-cyan-500",
    solidText: "text-[#0A111C]",
    ring: "ring-cyan-500/20",
    gradFrom: "from-cyan-500/15",
  },
  purple: {
    text: "text-purple-400",
    soft: "bg-purple-500/10",
    border: "border-purple-500/30",
    glow: "bg-purple-500/20",
    solid: "bg-purple-500",
    solidText: "text-[#0A111C]",
    ring: "ring-purple-500/20",
    gradFrom: "from-purple-500/15",
  },
};

export const PILLARS = {
  notes: {
    key: "notes" as const,
    name: "Live Notes",
    icon: NotebookPen,
    accent: "emerald" as Accent,
    href: "/live-notes",
    anchor: "live-notes",
    tagline: "Turn any conversation into clean, structured notes.",
    soon: false,
  },
  agent: {
    key: "agent" as const,
    name: "Live Cue",
    icon: Sparkles,
    accent: "purple" as Accent,
    href: "/live-agent",
    anchor: "live-agent",
    tagline: "An AI that understands the conversation and recommends your next move.",
    soon: true,
  },
  translator: {
    key: "translator" as const,
    name: "Live Translator",
    icon: Languages,
    accent: "cyan" as Accent,
    href: "/live-translator",
    anchor: "live-translator",
    tagline: "Understand and be understood, in 100+ languages.",
    soon: false,
  },
};

/* ------------------------------------------------------------------ */
/* Small building blocks                                              */
/* ------------------------------------------------------------------ */

export function Eyebrow({
  icon: Icon,
  children,
  accent,
}: {
  icon?: LucideIcon;
  children: React.ReactNode;
  accent?: Accent;
}) {
  const a = accent ? ACCENT[accent] : null;
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-[0.06em] ${
        a ? `${a.soft} ${a.border} ${a.text}` : "bg-card/60 border-white/10 text-muted-foreground"
      }`}
    >
      {Icon ? <Icon className="w-3.5 h-3.5" /> : <span className="w-1.5 h-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

export function SoonBadge({ children = "Coming Soon" }: { children?: React.ReactNode }) {
  return (
    <span className="text-[10px] font-semibold text-purple-300 bg-purple-500/15 border border-purple-500/30 rounded-full px-2.5 py-1 whitespace-nowrap">
      {children}
    </span>
  );
}

/* Neutral surface pill — used for "Concept" / "Coming soon · idea stage" */
export function NeutralBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] font-semibold text-muted-foreground bg-white/5 border border-white/10 rounded-full px-2.5 py-1 whitespace-nowrap">
      {children}
    </span>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  sub,
  accent,
}: {
  eyebrow?: React.ReactNode;
  title: string;
  sub?: string;
  accent?: Accent;
}) {
  return (
    <div className="text-center max-w-2xl mx-auto mb-14">
      {eyebrow ? <div className="mb-4 flex justify-center"><Eyebrow accent={accent}>{eyebrow}</Eyebrow></div> : null}
      <h2 className="text-3xl md:text-5xl font-bold font-display tracking-tight">{title}</h2>
      {sub ? <p className="text-muted-foreground text-lg mt-4 leading-relaxed">{sub}</p> : null}
    </div>
  );
}

export const APP_STORE_URL = "https://apps.apple.com/app/id6773778315";
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.coflows.traverba";

/* Store badges with the real Apple / Google Play marks.
 * direct: link straight to the stores instead of the /download page. */
export function StoreBadges({ track = "rebrand", direct }: { track?: string; direct?: boolean }) {
  const iosBadge = (
    <div className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-2.5 transition-colors cursor-pointer">
      <svg className="w-6 h-6 text-foreground" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
      </svg>
      <div className="text-left leading-tight">
        <div className="text-[10px] text-muted-foreground">Download on the</div>
        <div className="text-sm font-semibold">App Store</div>
      </div>
    </div>
  );
  const androidBadge = (
    <div className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-2.5 transition-colors cursor-pointer">
      <svg className="w-6 h-6 text-foreground" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
      </svg>
      <div className="text-left leading-tight">
        <div className="text-[10px] text-muted-foreground">Get it on</div>
        <div className="text-sm font-semibold">Google Play</div>
      </div>
    </div>
  );
  return (
    <div className="flex flex-col sm:flex-row items-center gap-3.5">
      {direct ? (
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackAppDownload("ios", track)}
        >
          {iosBadge}
        </a>
      ) : (
        <Link href="/download" onClick={() => trackCTA(`download_${track}_ios`, `${track} iOS`)}>
          {iosBadge}
        </Link>
      )}
      {direct ? (
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackAppDownload("android", track)}
        >
          {androidBadge}
        </a>
      ) : (
        <Link href="/download" onClick={() => trackCTA(`download_${track}_android`, `${track} Android`)}>
          {androidBadge}
        </Link>
      )}
    </div>
  );
}

/* Photographic product visual with an accent glow */
export function PhotoVisual({ accent, src, alt }: { accent: Accent; src: string; alt: string }) {
  const a = ACCENT[accent];
  return (
    <div className="relative flex-1 flex justify-center w-full">
      <div className={`absolute -inset-6 ${a.glow} rounded-full blur-3xl opacity-40`} />
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="relative z-10 w-full max-w-[560px] aspect-[7/5] object-cover rounded-2xl border border-white/10 shadow-2xl shadow-black/50"
      />
    </div>
  );
}

/* Alternating immersive feature row (text + visual) */
export function ImmersiveRow({
  accent,
  eyebrowIcon,
  eyebrowLabel,
  badge,
  title,
  desc,
  bullets,
  visual,
  reverse,
  cta,
  anchor,
}: {
  accent: Accent;
  eyebrowIcon: LucideIcon;
  eyebrowLabel: string;
  badge?: string;
  title: string;
  desc: string;
  bullets: { icon: LucideIcon; text: string }[];
  visual: React.ReactNode;
  reverse?: boolean;
  cta?: { label: string; href: string; ghost?: boolean };
  anchor?: string;
}) {
  const a = ACCENT[accent];
  return (
    <motion.div
      id={anchor}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className={`scroll-mt-28 flex flex-col ${reverse ? "md:flex-row-reverse" : "md:flex-row"} items-center gap-12 md:gap-16`}
    >
      <div className="flex-1 space-y-5">
        <div className="flex items-center gap-3">
          <Eyebrow icon={eyebrowIcon} accent={accent}>{eyebrowLabel}</Eyebrow>
          {badge ? <NeutralBadge>{badge}</NeutralBadge> : null}
        </div>
        <h3 className="text-3xl md:text-4xl font-bold font-display tracking-tight">{title}</h3>
        <p className="text-muted-foreground text-lg leading-relaxed max-w-xl">{desc}</p>
        <ul className="space-y-3 pt-1">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-center gap-3 text-foreground/85">
              <span className={`w-8 h-8 rounded-lg ${a.soft} flex items-center justify-center shrink-0`}>
                <b.icon className={`w-4 h-4 ${a.text}`} />
              </span>
              <span>{b.text}</span>
            </li>
          ))}
        </ul>
        {cta ? (
          <div className="pt-2">
            <Link
              href={cta.href}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                cta.ghost ? `${a.soft} ${a.border} border ${a.text} hover:bg-white/5` : `${a.solid} ${a.solidText} hover:opacity-90`
              }`}
            >
              {cta.label} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : null}
      </div>
      {visual}
    </motion.div>
  );
}

/* Full-bleed section wrapper with alternating background + edge glow */
export function ImmersiveSection({
  bgSoft,
  accent,
  reverse,
  anchor,
  children,
}: {
  bgSoft?: boolean;
  accent: Accent;
  reverse?: boolean;
  anchor?: string;
  children: React.ReactNode;
}) {
  const a = ACCENT[accent];
  return (
    <section id={anchor} className={`relative overflow-hidden py-20 md:py-28 ${bgSoft ? "bg-[#0D1626]" : "bg-background"}`}>
      <div className={`absolute top-1/4 ${reverse ? "left-0" : "right-0"} w-[480px] h-[420px] ${a.glow} rounded-full blur-3xl opacity-40 pointer-events-none`} />
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">{children}</div>
      </div>
    </section>
  );
}

/* Compact feature card */
export function FeatureCard({
  icon: Icon,
  title,
  desc,
  accent,
  badge,
}: {
  icon: LucideIcon;
  title: string;
  desc: string;
  accent: Accent;
  badge?: string;
}) {
  const a = ACCENT[accent];
  return (
    <div className="h-full rounded-2xl border border-white/10 bg-gradient-to-b from-[#1B2538] to-[#141C2B] p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className={`w-11 h-11 rounded-xl ${a.soft} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${a.text}`} />
        </div>
        {badge ? <SoonBadge>{badge}</SoonBadge> : null}
      </div>
      <h4 className="font-semibold font-display text-base">{title}</h4>
      <p className="text-sm text-muted-foreground leading-snug">{desc}</p>
    </div>
  );
}

/* How it works — numbered steps (optional per-step accent) */
export function HowItWorks({
  eyebrow,
  title,
  sub,
  steps,
  accent,
}: {
  eyebrow?: React.ReactNode;
  title: string;
  sub?: string;
  steps: { icon: LucideIcon; title: string; desc: string; accent?: Accent }[];
  accent: Accent;
}) {
  return (
    <section className="py-24 bg-[#0D1626]">
      <div className="container mx-auto px-4">
        <SectionHeading eyebrow={eyebrow} title={title} sub={sub} accent={accent} />
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {steps.map((s, i) => {
            const a = ACCENT[s.accent ?? accent];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.12 }}
                viewport={{ once: true }}
                className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#1B2538] to-[#141C2B] p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl ${a.soft} flex items-center justify-center`}>
                    <s.icon className={`w-6 h-6 ${a.text}`} />
                  </div>
                  <span className="text-3xl font-bold font-display text-white/10">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-bold font-display text-lg mb-2">{s.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* Persona / feature grid — 4 cards, optional subtitle */
export function WhoItsFor({
  title,
  sub,
  items,
  accent,
}: {
  title: string;
  sub?: string;
  items: { icon: LucideIcon; title: string; desc: string }[];
  accent: Accent;
}) {
  const a = ACCENT[accent];
  return (
    <section className="py-24">
      <div className="container mx-auto px-4">
        <SectionHeading title={title} sub={sub} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
          {items.map((it, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              viewport={{ once: true }}
              className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#1B2538] to-[#141C2B] p-5"
            >
              <div className={`w-11 h-11 rounded-xl ${a.soft} flex items-center justify-center mb-3`}>
                <it.icon className={`w-5 h-5 ${a.text}`} />
              </div>
              <h4 className="font-semibold font-display mb-1.5">{it.title}</h4>
              <p className="text-sm text-muted-foreground leading-snug">{it.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* CTA band — gradient card */
export function CtaBand({
  title,
  sub,
  button,
  href = "/download",
  accent,
  note,
}: {
  title: string;
  sub: string;
  button: string;
  href?: string;
  accent: Accent;
  note?: string;
}) {
  const a = ACCENT[accent];
  return (
    <section className="py-24 bg-[#0D1626]">
      <div className="container mx-auto px-4">
        <div className={`relative overflow-hidden rounded-3xl border ${a.border} bg-gradient-to-br ${a.gradFrom} to-transparent p-12 md:p-16 text-center`}>
          <div className={`absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[300px] ${a.glow} rounded-full blur-3xl opacity-50`} />
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold font-display tracking-tight mb-4">{title}</h2>
            <p className="text-muted-foreground text-lg mb-8">{sub}</p>
            {/^https?:/.test(href) ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCTA("cta_band", title)}
                className={`inline-flex items-center gap-2 rounded-xl px-7 py-3.5 text-base font-semibold ${a.solid} ${a.solidText} hover:opacity-90 transition-opacity`}
              >
                {button} <ArrowRight className="w-4 h-4" />
              </a>
            ) : (
              <Link
                href={href}
                onClick={() => trackCTA("download_cta_band", title)}
                className={`inline-flex items-center gap-2 rounded-xl px-7 py-3.5 text-base font-semibold ${a.solid} ${a.solidText} hover:opacity-90 transition-opacity`}
              >
                {button} <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            {note ? <p className="text-muted-foreground text-xs mt-5">{note}</p> : null}
          </div>
        </div>
      </div>
    </section>
  );
}

/* FAQ list.
 *
 * Uses native <details>/<summary> rather than the state-toggled accordion in
 * Home.tsx: that one unmounts the answer while collapsed, so the text is absent
 * from the DOM and neither crawlers nor the FAQPage rich result can see it.
 * <details> keeps every answer in the DOM, collapsed, and is keyboard- and
 * screen-reader-accessible with no extra code. */
export function FaqList({ items }: { items: { q: string; a: string }[] }) {
  return (
    <div className="max-w-3xl mx-auto">
      {items.map((f) => (
        <details key={f.q} className="group border-b border-white/10">
          <summary className="flex items-center justify-between gap-4 py-5 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
            <h3 className="font-semibold font-display text-base md:text-lg">{f.q}</h3>
            <Plus className="w-5 h-5 text-muted-foreground shrink-0 transition-transform group-open:rotate-45 group-open:text-primary" />
          </summary>
          <p className="text-muted-foreground leading-relaxed pb-5 max-w-3xl">{f.a}</p>
        </details>
      ))}
    </div>
  );
}

/* shared CheckCircle for convenience */
export { CheckCircle2 };
